import React from 'react';
import { Building2 } from 'lucide-react';
import LoanPageTemplate from '../../components/LoanPageTemplate';

const InvestmentProperty = () => (
  <LoanPageTemplate
    guides={[
      { title: 'How Much Can I Borrow?', href: '/blog/borrowing-power', blurb: 'How lenders assess income, expenses and the serviceability buffer.' },
      { title: 'Fixed vs Variable Rates', href: '/blog/fixed-vs-variable', blurb: 'How fixed, variable and split loans compare, and what break costs mean for you.' },
      { title: 'Mortgage Broker vs Bank', href: '/blog/broker-vs-bank', blurb: 'What a broker does, how brokers are paid and when going direct makes sense.' },
    ]}
    icon={Building2}
    path="/investment-property"
    title="Investment Property Loans"
    subtitle="Support your property goals with the right loan structure and expert guidance. We help you understand investment lending requirements and options."
    description="Investment property loans are often assessed differently to owner-occupied mortgages, sometimes with higher interest rates, stricter serviceability buffers, and unique tax considerations. Getting the structure right from the start can mean the difference between a positively geared asset and a financial burden."
    features={[
      {
        title: 'Negative Gearing Strategy',
        description: 'We structure your investment loan to maximise legitimate tax deductions including interest, depreciation, and property expenses. Interest-only repayments keep deductions high in the early years.'
      },
      {
        title: 'Interest-Only Loan Options',
        description: 'Most investment lenders offer interest-only periods of 1-5 years, reducing your cash outflow and improving rental yield. We help you understand when interest-only makes sense and when it does not.'
      },
      {
        title: 'SMSF Property Lending',
        description: 'Purchase investment property through your Self-Managed Super Fund with a limited recourse borrowing arrangement. We access specialist SMSF lenders with competitive rates and understand the compliance requirements.'
      },
      {
        title: 'Portfolio Lending & Cross-Collateralisation',
        description: 'As your portfolio grows, loan structure becomes critical. We advise on standalone versus cross-collateralised security and help you avoid common traps that limit future borrowing capacity.'
      },
      {
        title: 'Rental Yield & Cash Flow Modelling',
        description: 'Before you commit, we model the after-tax cash flow of your investment including rental income, loan repayments, strata, insurance, and maintenance to ensure the numbers work.'
      },
      {
        title: 'Equity Access for Multiple Properties',
        description: 'Leverage equity in your existing home or investment properties to fund deposits on additional purchases. We map out a staged acquisition plan aligned with your borrowing capacity.'
      }
    ]}
    process={[
      {
        title: 'Investment Strategy Discussion',
        description: 'We discuss your goals, risk tolerance, and existing portfolio to determine the right loan structure. This includes whether to hold in personal names, a trust, or your SMSF.'
      },
      {
        title: 'Borrowing Capacity & Structuring',
        description: 'Investment loans are assessed at higher serviceability rates. We calculate your true borrowing power and structure the loan to preserve capacity for future acquisitions.'
      },
      {
        title: 'Lender Selection & Approval',
        description: 'Not all lenders treat investment loans equally. We match you with lenders that offer the best combination of rate, policy, and appetite for your specific property type and location.'
      },
      {
        title: 'Settlement & Tax Setup',
        description: 'We ensure your loan settles with the correct structure for tax purposes and provide documentation your accountant needs for depreciation schedules and interest deduction claims.'
      }
    ]}
    faqs={[
      {
        q: 'Are investment property loan rates higher than owner-occupied rates?',
        a: 'Yes. Investment loans typically carry a rate premium of 0.20% to 0.50% above owner-occupied rates. This is because regulators require banks to hold more capital against investment lending. However, the gap varies significantly between lenders, and we find the most competitive investment rates on our panel.'
      },
      {
        q: 'What is negative gearing and how does it reduce my tax?',
        a: 'Negative gearing occurs when your investment property expenses (interest, depreciation, maintenance, insurance) exceed your rental income. The resulting loss can be offset against your salary or business income, reducing your overall tax bill. For example, if your property generates a $10,000 annual loss and your marginal tax rate is 37%, you receive a $3,700 tax benefit.'
      },
      {
        q: 'Should I choose interest-only or principal and interest repayments?',
        a: 'Interest-only repayments are popular with investors because they maximise cash flow and tax deductions in the early years. However, they mean you are not reducing the loan balance. The right choice depends on your tax position, cash flow needs, and long-term strategy. We model both scenarios so you can make an informed decision.'
      },
      {
        q: 'Can I buy an investment property through my SMSF?',
        a: 'Yes, your SMSF can borrow to purchase residential or commercial property using a limited recourse borrowing arrangement (LRBA). The property must meet the sole purpose test, meaning it cannot be lived in by you or your relatives. SMSF loans typically require a 20-30% deposit and are offered by specialist lenders. We handle the entire process including trustee structure requirements.'
      },
      {
        q: 'How many investment properties can I finance?',
        a: 'There is no fixed limit, but each additional property reduces your remaining borrowing capacity. Some lenders cap the number of financed properties at 4-6, while others will lend on larger portfolios. We work with specialist lenders who support portfolio investors and structure each loan to preserve capacity for future growth.'
      }
    ]}
    loanType="Investment Property"
    color="from-amber-600 to-amber-800"
  />
);

export default InvestmentProperty;
